import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';
import _ from 'lodash';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core';

import * as Constants from '../../../../../constants/charts';
import {
  API_BASED_USAGE,
  API_PER_ORG_BASED_USAGE,
  ORG_BASED_USAGE,
} from '../../../../../constants/metrics';
import { getI18n } from "../../../../../utils/intl";

import {
  getBucketPermuteId,
  getDefaultSharedMutations,
  getMetricKey,
  getSourceFromMetricKey,
  getMetricFromMetricKey,
  getEntityNamesMapFromUsageResponse,
  getNamesWithPermuteIdAndEntitiesMap,
} from '../../../utils/permute';
import { getXValueTick } from '../../../utils/filter';
import {
  getUsageRangeData, getDisplayDate, getMonthlyUsageParamsFromQueryData,
  lineColors, lightShadeLineColors, getParamsForHistoricalUsageData,
  clearHistoricalDataMutations,
  clearMetricsHoverState,
} from '../../../utils';

import Loading from "../../../../Loading";
import CompositeXYChart from '../../../CompositeXYChart';
import ChartLegend from '../../../ChartLegend';
import CustomLabelComponent from '../../../CustomLabelComponent';
import NoData from '../../../NoData';
import ChartError from '../../../ChartError';

import { TooltipStyle } from '../../../ChartTooltip/index.theme';
import styles from './styles';

const USAGE_METRIC_DEFAULT = 'default';
const USAGE_METRIC_CUSTOM_DATE = 'customDate';
const USAGE_METRIC_CUSTOM_WEEK = 'customWeek';

const MonthlyQuotaChart = (props) => {
  const {
    isLoading, apiMap, orgMap, defaultUsageResponse,
    usageParams, fetchCustomDateMetricsData,
    is30DaysDataLoading, is52WeeksDataLoading,
    usageResponseFor30DaysAgo, usageResponseFor52WeeksAgo,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    sharedMutations: {},
    keyMetricDataTS: 0,
    key: '',
    metricEvent: '',
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const defaultUsageResponseRef = useRef(defaultUsageResponse);
  useEffect(() => {
    const { usageType } = usageParams || {};
    const oldSourcesIds = (state.sharedMutations && keys(state.sharedMutations)) || [];
    const entitiesStr = getEntitiesString(usageType);
    const newSourcesIds = getSourcesIdsFromResponse(defaultUsageResponse, 'usageTs', entitiesStr);

    const hasNewSourceIds = newSourcesIds.length > 0;
    // reset sharedMutations if sources changes
    if (hasNewSourceIds && !isEqual(oldSourcesIds.sort(), newSourcesIds.sort())) {
      const sharedMutations = getDefaultSharedMutations(newSourcesIds, USAGE_METRIC_DEFAULT);
      setState({
        sharedMutations,
        keyMetricDataTS: defaultUsageResponse.timeStamp,
      });
    } else if (hasNewSourceIds && defaultUsageResponse.timeStamp > state.keyMetricDataTS) {
      setState({
        sharedMutations: clearHistoricalDataMutations(state.sharedMutations),
        keyMetricDataTS: defaultUsageResponse.timeStamp,
      });
    }
    defaultUsageResponseRef.current = defaultUsageResponse;
  }, [defaultUsageResponse]);

  useEffect(() => {
    setState(prevState => {
      const { key, metricEvent, sharedMutations= {} } = prevState
      const sourceKey = metricEvent ? getSourceFromMetricKey(key) : key;
      const sourceMutation = (sharedMutations && sharedMutations[sourceKey]) || {};
      const metricKey = getMetricFromMetricKey(key);
      const metricsMutation = sourceMutation.metrics || {};
      const metricMutation = metricsMutation[metricKey];
      const defaultResponse = defaultUsageResponse;
      const { usageType } = usageParams || {};
      const entitiesStr = getEntitiesString(usageType);
      const permuteIds = getSourcesIdsFromResponse(defaultResponse, 'usageTs', entitiesStr);
      const { apiIds, orgIds } = getEntityIdsFromPermuteIds(permuteIds, usageType);
      if (defaultResponse && defaultResponse.query) {
        if ((metricKey === USAGE_METRIC_CUSTOM_DATE) && metricMutation.show) {
          const params = getParamsForHistoricalUsageData(
            defaultResponse.query,
            Constants.HISTORICAL_METRIC_30DAYS,
            apiIds,
            orgIds,
            usageType,
            Constants.CHART_QUOTA_MONTHLY,
          );
          fetchCustomDateMetricsData(params);
        }
        if ((metricKey === USAGE_METRIC_CUSTOM_WEEK) && metricMutation.show) {
          const params = getParamsForHistoricalUsageData(
            defaultResponse.query,
            Constants.HISTORICAL_METRIC_52WEEKS,
            apiIds,
            orgIds,
            usageType,
            Constants.CHART_QUOTA_MONTHLY,
          );
          fetchCustomDateMetricsData(params);
        }
      }
    });
  }, [state.key, state.metricEvent]);

  const getEntitiesString = (usageType) => {
    let entitiesStr = 'orgs';
    if (usageType === API_BASED_USAGE) {
      entitiesStr = 'apis';
    } else if (usageType === ORG_BASED_USAGE) {
      entitiesStr = 'orgs';
    } else if (usageType === API_PER_ORG_BASED_USAGE) {
      entitiesStr = 'apis,orgs';
    }
    return entitiesStr;
  };

  const getSourcesIdsFromResponse = (response = {}, type, entitiesStr) => {
    const responseData = response || {};
    const data = responseData.data || {};
    const usageTs = (data && data[type]) || [];
    const sourceIdsMap = {};
    if (entitiesStr) {
      _.each(usageTs, usageT => {
        _.each(usageT.usage, usageObj => {
          const permuteId = getBucketPermuteId(usageObj, entitiesStr);
          sourceIdsMap[permuteId] = 1;
        });
      });
    } else {
      sourceIdsMap['1'] = 1;
    }
    return _.keys(sourceIdsMap);
  };

  const getEntityIdsFromPermuteIds = (permuteIds, usageType) => {
    let apiIds = [];
    let orgIds = [];
    if (usageType === API_BASED_USAGE) {
      apiIds = permuteIds;
    } else if (usageType === ORG_BASED_USAGE) {
      orgIds = permuteIds;
    } else if (usageType === API_PER_ORG_BASED_USAGE) {
      forEach(permuteIds, permueId => {
        const ids = permueId.split('__');
        apiIds.push(ids[0]);
        orgIds.push(ids[1]);
      });
    }
    return { apiIds, orgIds };
  };

  const getHistoricalChartDataFromResponse = (usageResponse) => {
    const queryParams = getMonthlyUsageParamsFromQueryData(
      usageResponse && usageResponse.query,
    );
    const timerange = queryParams.timerange;
    const timeAgg = queryParams.timeAgg;
    const startDate = queryParams.startDate;
    const endDate = queryParams.endDate;
    const dateRangeData = getUsageRangeData(
      timerange,
      timeAgg,
      startDate,
      endDate,
    );
    const historicalChartDataFromResponse = getUsageData(
      dateRangeData,
      usageResponse,
    );
    return historicalChartDataFromResponse;
  };

  const getI18nStrings = (key) => {
    let i18nString = '';
    if (!_.isUndefined(key)) {
      const strObj = {
        dateTimeFormat: 'label.analytics.date.time.format.text',
        consumption: 'label.analytics.quota.consumption.text',
        quotaLimit: 'label.analytics.quota.limit.text',
        apiHits: 'label.analytics.api.hits.text',
        noDataText: 'label.analytics.dashboard.noData.text',
      };
      i18nString = intl.getI18nMessage(strObj[key]);
    }
    return i18nString;
  };

  const getTooltipLabel = (usageObj, entityNames, dateTimeStr, quotaLimit) => {
    const noDataStr = intl.getI18nMessage('label.analytics.dashboard.noData.text');
    let consumptionStr = usageObj ? (Math.round(usageObj.usage * 100) / 100).toFixed(2) : '0';
    // Showing No Data for consumption if quota is undefined
    consumptionStr = (usageObj && usageObj.monthlyQuota) ? `${consumptionStr}%` : noDataStr;
    const usageStr = (usageObj && usageObj.hits) || noDataStr;
    const monthlyQuotaStr = (usageObj && usageObj.monthlyQuota) || quotaLimit;
    const tooltipLabel = `${entityNames.join(' > ')}\n\n${getI18nStrings('apiHits')} : ${usageStr} \n${getI18nStrings('quotaLimit')} : ${monthlyQuotaStr} \n${getI18nStrings('consumption')} : ${consumptionStr} \n${getI18nStrings('dateTimeFormat')} : \n ${dateTimeStr}`;
    return tooltipLabel;
  };

  const getUsageData = (xAxisData, usageResponse) => {
    let maxYValue = 0;
    const entitiesMap = { orgMap, apiMap };
    const { usageType } = usageParams || {};
    const permuteUsageTs =
      usageResponse && usageResponse.data && usageResponse.data.usageTs;
    const sortedPermuteUsageTs =
      (permuteUsageTs && permuteUsageTs.sort((a, b) => (a.date > b.date ? 1 : -1))) || [];
    const entitiesStr = getEntitiesString(usageType);
    const entityMapFromResponse =
      getEntityNamesMapFromUsageResponse(usageResponse && usageResponse.data, entitiesStr);
    const {
      timerange,
      timeAgg,
      startDate,
      endDate,
    } = getMonthlyUsageParamsFromQueryData(usageResponse && usageResponse.query);

    // collect entity or permutation(combination of entities) ids
    const permuteIds = getSourcesIdsFromResponse(usageResponse, 'usageTs', entitiesStr);

    // build quota limit maps for all permuteIds
    // needed for fixing DE436128 as we don't get that
    // data in timeStamped objects with no data
    const quotaLimitMap = {};
    sortedPermuteUsageTs.forEach((timeBasedUsage = {}) => {
      const { usage = [] } = timeBasedUsage;
      usage.forEach(item => {
        const permuteId = getBucketPermuteId(item, entitiesStr);
        quotaLimitMap[permuteId] = item.monthlyQuota;
      });
    });

    // build chart data for each permuteId
    const chartData = [];
    permuteIds.forEach((permuteId, idx) => {
      const data = [];

      let entitiesNamesArr = entityMapFromResponse[permuteId];
      if (!entitiesNamesArr || (entitiesNamesArr.length === 0)) {
        entitiesNamesArr = getNamesWithPermuteIdAndEntitiesMap(
          permuteId, entitiesStr, entitiesMap,
        );
      }

      // go over all timestamp based data objects,
      // collect data for the entity or permutations of entities denoted by permuteId
      sortedPermuteUsageTs.forEach(timeBasedUsages => {
        // get data Object for the permuteId in time stamped object
        const permuteUsageBucket = find(timeBasedUsages.usage, bucket => {
          const currentBucketPermuteId = getBucketPermuteId(bucket, entitiesStr);
          return currentBucketPermuteId === permuteId;
        });

        // get yValue
        const yValue = permuteUsageBucket && permuteUsageBucket.usage ?
          permuteUsageBucket.usage : 0;

        // get xValue
        let date = timeBasedUsages.date;
        if (timerange === 'week') {
          const startOfDay = moment.utc(date).startOf('day');
          const dateISOString = new Date(startOfDay).toISOString();
          date = `${dateISOString.split('.')[0]}Z`;
        }
        const xValue = xAxisData &&
          getXValueTick(xAxisData.xAxisValuesTickMap, date);

        // get tooltip label
        const dateTimeStr =
          getDisplayDate(moment.utc(timeBasedUsages.date).toISOString(),
          timerange, timeAgg, startDate, endDate, 'tooltip', true);
        const tooltipLabel = getTooltipLabel(
          permuteUsageBucket, entitiesNamesArr, dateTimeStr, quotaLimitMap[permuteId] || 0,
        );

        const dataObj = {
          x: xValue,
          y: yValue,
          label: tooltipLabel,
        };
        data.push(dataObj);
        maxYValue = yValue > maxYValue ? yValue : maxYValue;
      });

      if (data.length) {
        chartData.push({
          data,
          customLabelComponent: createCustomLabelComponent(TooltipStyle),
          id: permuteId,
          names: entitiesNamesArr,
          type: Constants.CHART_ELEMENT_LINE,
          colorIndex: idx,
        });
      }
    });
    return chartData;
  };

  const getLineChartsData = (usageData, metric) => {
    const lineCharts = [];
    forEach(usageData, item => {
      const { id, data, customLabelComponent } = item;
      let { colorIndex } = item;
      let pattern = '';
      let useLightShadedColor = false;
      const lineData = [];
      const mutations = state.sharedMutations;
      const sourceMutation = mutations[id] || {};
      if (sourceMutation && sourceMutation.colorIndex !== undefined) {
        colorIndex = sourceMutation.colorIndex;
      }
      const metricsMutation = sourceMutation.metrics || {};
      if (metric === USAGE_METRIC_CUSTOM_DATE) {
        pattern = 'dashed';
      }
      if (metric === USAGE_METRIC_CUSTOM_WEEK) {
        useLightShadedColor = true;
      }
      const metricMutation = metricsMutation[metric] || {};
      const highLight = !!metricMutation.hover;
      if (!sourceMutation.show || !metricMutation.show) {
        return;
      }
      forEach(data, dataItem => {
        const { x, y, label } = dataItem;
        lineData.push({
          label,
          x,
          y,
        });
      });
      lineCharts.push({
        colorIndex,
        customLabelComponent,
        data: lineData,
        pattern,
        highLight,
        useLightShadedColor,
        id,
        type: Constants.CHART_ELEMENT_LINE,
        metric,
      });
    });
    return lineCharts;
  };

  const getUsageLegend = (color, isLine, show, pattern) => {
    const legendLineItemClass =
      pattern === 'dashed'
        ? classes.lineLegendItem__dashed
        : classes.lineLegendItem;
    return (<div
      className={
        isLine
          ? legendLineItemClass
          : classes.blockLegendItem
      }
      style={{
        backgroundColor: pattern === 'dashed' ? 'inherit' : color,
        visibility: show ? 'visible' : 'hidden',
        border: pattern === 'dashed' ? `2px dashed ${color}` : 'none',
      }}
    />);
  };

  const getUsageLegendData = (
    usageChartData,
    usageDataFor30DaysAgo,
    usageDataFor52WeeksAgo,
  ) => {
    const usageLegendData = [];
    const { sharedMutations } = state;
    forEach(usageChartData, (item, key) => {
      const sourceMutation = sharedMutations[item.id] || {};
      const sourceShow = !!sourceMutation.show;
      const metricsObj = (sourceMutation && sourceMutation.metrics) || {};
      const defaultMetricKey = getMetricKey(item.id, USAGE_METRIC_DEFAULT);
      const daysMetricKey = getMetricKey(item.id, USAGE_METRIC_CUSTOM_DATE);
      const weeksMetricKey = getMetricKey(item.id, USAGE_METRIC_CUSTOM_WEEK);
      let dataArr = item.data;
      const defaultLegendValue = (dataArr && _.isArray(dataArr) && dataArr.length > 0 &&
        dataArr[dataArr.length - 1] && dataArr[dataArr.length - 1].y) || '';
      let legend30DaysAgoValue = '';
      let legend52WeeksAgoValue = '';
      if (is30DaysDataLoading) {
        legend30DaysAgoValue = 'loading';
      } else {
        const usage30DaysAgo = usageDataFor30DaysAgo &&
          usageDataFor30DaysAgo.find(data => data.id === item.id);
        dataArr = usage30DaysAgo && usage30DaysAgo.data;
        legend30DaysAgoValue = (dataArr && _.isArray(dataArr) && dataArr.length > 0 &&
          dataArr[dataArr.length - 1] && dataArr[dataArr.length - 1].y) || '';
      }
      if (is52WeeksDataLoading) {
        legend52WeeksAgoValue = 'loading';
      } else {
        const usage52WeeksAgo = usageDataFor52WeeksAgo &&
          usageDataFor52WeeksAgo.find(data => data.id === item.id);
        dataArr = usage52WeeksAgo && usage52WeeksAgo.data;
        legend52WeeksAgoValue = (dataArr && _.isArray(dataArr) && dataArr.length > 0 &&
          dataArr[dataArr.length - 1] && dataArr[dataArr.length - 1].y) || '';
      }
      if (usageResponseFor30DaysAgo && !legend30DaysAgoValue) {
        legend30DaysAgoValue = '0.00%';
      }
      if (usageResponseFor52WeeksAgo && !legend52WeeksAgoValue) {
        legend52WeeksAgoValue = '0.00%';
      }
      const daysMetricShow = !!(
        sourceShow &&
        metricsObj[USAGE_METRIC_CUSTOM_DATE] &&
        sourceShow &&
        metricsObj[USAGE_METRIC_CUSTOM_DATE].show
      );
      const weeksMetricShow = !!(
        sourceShow &&
        metricsObj[USAGE_METRIC_CUSTOM_WEEK] &&
        sourceShow &&
        metricsObj[USAGE_METRIC_CUSTOM_WEEK].show
      );
      const individualData = [];
      const color = lineColors[key % lineColors.length];
      const lightShadeColor = lightShadeLineColors[key % lightShadeLineColors.length];
      individualData.push(
        {
          id: defaultMetricKey,
          label: 'label.analytics.quota.usage.text',
          value: defaultLegendValue,
          component: getUsageLegend(color, true, sourceShow),
          show: sourceShow,
        },
        {
          id: daysMetricKey,
          label: 'label.analytics.trafficChart.legend.30daysLabel.text',
          value: (daysMetricShow && legend30DaysAgoValue) || '',
          component: getUsageLegend(color, true, daysMetricShow, 'dashed'),
          show: daysMetricShow,
        },
        {
          id: weeksMetricKey,
          label: 'label.analytics.trafficChart.legend.52weeksLabel.text',
          value: (weeksMetricShow && legend52WeeksAgoValue) || '',
          component: getUsageLegend(lightShadeColor, true, weeksMetricShow),
          show: weeksMetricShow,
        },
      );
      usageLegendData.push({
        uuid: item.id,
        names: item.names,
        color,
        data: individualData,
        suffix: '%',
        show: sourceShow,
      });
    });

    return usageLegendData;
  };

  const chartHoverHandler = (id, metric, hoverState) => {
    legendHoverHandler(getMetricKey(id, metric), hoverState);
  }

  const createCustomLabelComponent = (styles) => {
    const StyledCustomLabelComponent = CustomLabelComponent(styles);
    return <StyledCustomLabelComponent />;
  };

  const legendHoverHandler = (key, hoverState) => {
    setState(prevState => {
      let sharedMutations = prevState.sharedMutations || {};
      const source = getSourceFromMetricKey(key);
      const sourceMutation = (sharedMutations && sharedMutations[source]) || {};
      const metric = getMetricFromMetricKey(key);
      const metricsMutation = (sourceMutation && sourceMutation.metrics) || {};
      const metricMutation = (metricsMutation && metricsMutation[metric]) || {};
      if (hoverState) {
        // DE437921: we started using hover state to enable/disable tooltip
        // But, with VictoryVoronoiContainer data points from different metrics
        // can be in hover state, when they have same values. so, i am
        // clearing hover state of all metrics to keep only the latest one
        // as highlighted(i.e. only it's tooltips will be active).
        // so user would see only 1 tooltip
        sharedMutations = clearMetricsHoverState(sharedMutations);
      }
      const updatedMutations = {
        ...sharedMutations,
        [source]: {
          ...sourceMutation,
          metrics: {
            ...metricsMutation,
            [metric]: {
              ...metricMutation,
              hover: hoverState,
            },
          },
        },
      };
      return {
        sharedMutations: updatedMutations,
      };
    });
  };

  const legendClickHandler = (key, metricEvent) => {
    const {
      timerange,
      timeAgg,
      startDate,
      endDate,
    } = getMonthlyUsageParamsFromQueryData(defaultUsageResponse && defaultUsageResponse.query);
    const dateRangeData = getUsageRangeData(
      timerange,
      timeAgg,
      startDate,
      endDate,
    );
    const usageChartData = getUsageData(
      dateRangeData,
      defaultUsageResponse,
    );
    // this shouldn't be required if it is set on props change
    const colorIndex = usageChartData.find(
      data => {
        const id = getSourceFromMetricKey(data.id);
        return id === getSourceFromMetricKey(key);
      },
    ).colorIndex;
    setState(prevState => {
      const sharedMutations = prevState.sharedMutations || {};
      const sourceKey = metricEvent ? getSourceFromMetricKey(key) : key;
      const sourceMutation = (sharedMutations && sharedMutations[sourceKey]) || {};
      const metricKey = getMetricFromMetricKey(key);
      const metricsMutation = sourceMutation.metrics || {};
      let updatedMutations = sharedMutations;
      if (metricEvent) {
        const metric = getMetricFromMetricKey(key);
        if (metric === USAGE_METRIC_DEFAULT) {
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              ...sourceMutation,
              show: !sourceMutation.show,
              metrics: {
                [metricKey]: {
                  show: !sourceMutation.show,
                },
              },
            },
          };
        } else {
          const metricMutation = metricsMutation[metricKey] || {};
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              show: true,
              metrics: {
                ...metricsMutation,
                default: { show: true },
                [metricKey]: {
                  show: !metricMutation.show,
                },
              },
              colorIndex,
            },
          };
        }
      }
      return {
        sharedMutations: updatedMutations,
        key, metricEvent,
      };
    });
  };

  const renderMessageComponent = (showError, errorTitle, errorMessage) => (
    showError ? (
      <ChartError title={errorTitle} message={errorMessage} />
    ) : (
      <NoData />
    )
  );

  const renderChartComponent = () => {
    if (isLoading) {
      return (
        <div className={classes.loader}>
          <Loading />
        </div>
      )
    }
    return (
      <div className={classes.chartContainer}>
        <div className={classes.lineChartContainer}>
          <div className={classes.plcaeholderText}>
            {intl.getI18nMessage('label.analytics.monthlyQuota.placeholder.title.text')}
          </div>
          <CompositeXYChart
            chartData={lineChartsData}
            chartScale={chartScale}
            formatXAxisData={(item) => getXAxisFormatter(item, dateRangeData)}
            hoverHandler={chartHoverHandler}
            lightShadeLineColors={lightShadeLineColors}
            lineColors={lineColors}
            xAxisLabel={getI18nStrings('dateTimeFormat')}
            xAxisValues={dateRangeData.xAxisTickValues}
            yAxisLabel={yAxisLabel}
          />
        </div>
        <div className={clsx(classes.labelForPrint, classes.errorRateChartLabel)}>
          {intl.getI18nMessage('label.analytics.lineChart.metric.text')}
        </div>
        <ChartLegend
          data={usageLegendData}
          hoverHandler={legendHoverHandler}
          clickHandler={legendClickHandler}
        />
      </div>
    );
  }

  const yAxisLabel = `${getI18nStrings('consumption')} (%)`;
  const chartScale = { x: 'time' };

  const getXAxisFormatter = (item, dateRangeData) => {
    const xAxisTickValuesMap = dateRangeData.xAxisTickValuesMap;
    if (xAxisTickValuesMap[item]) {
      return getDisplayDate(xAxisTickValuesMap[item], dateRangeData.dateRange,
        dateRangeData.timeAgg, dateRangeData.startDate, dateRangeData.endDate, 'axisLabel', true);
    }
    return '';
  };

  const { timerange, timeAgg, startDate, endDate } = getMonthlyUsageParamsFromQueryData(
    defaultUsageResponse && defaultUsageResponse.query,
  );

  const dateRangeData = getUsageRangeData(timerange, timeAgg, startDate, endDate);
  let usageChartData = getUsageData(
    dateRangeData,
    defaultUsageResponse,
  );
  const lineChartsData = getLineChartsData(usageChartData, USAGE_METRIC_DEFAULT) || [];
  const historicalChartDataFor30Days = [];
  const historicalChartDataFor52Weeks = [];
  if (usageResponseFor30DaysAgo) {
    historicalChartDataFor30Days.push(...getHistoricalChartDataFromResponse(
      usageResponseFor30DaysAgo),
    );
    const lineChartDataFor30Days = getLineChartsData(
      historicalChartDataFor30Days,
      USAGE_METRIC_CUSTOM_DATE,
    );
    lineChartsData.push(...lineChartDataFor30Days);
  }
  if (usageResponseFor52WeeksAgo) {
    historicalChartDataFor52Weeks.push(...getHistoricalChartDataFromResponse(
      usageResponseFor52WeeksAgo),
    );
    const lineChartDataFor52Weeks = getLineChartsData(
      historicalChartDataFor52Weeks,
      USAGE_METRIC_CUSTOM_WEEK,
    );
    lineChartsData.push(...lineChartDataFor52Weeks);
  }
  const usageLegendData = getUsageLegendData(
      usageChartData,
      historicalChartDataFor30Days,
      historicalChartDataFor52Weeks,
    );
  let showError = false;
  let errorMessage = '';
  let errorTitle = '';
  const usageParamsTimeRange = usageParams && usageParams.timerange;
  if ((timerange === 'hour') || (timerange === 'day') ||
      (usageParamsTimeRange === 'hour') || (usageParamsTimeRange === 'day')) {
    showError = true;
    errorTitle = intl.getI18nMessage('label.analytics.usage.error.unsupported.timeperiod.title');
    errorMessage = intl.getI18nMessage('label.analytics.usage.monthly.error.24hours.text');
    usageChartData = [];
  }

  return (
    <div
      className={classes.errorRateChartContainer}
      data-apim-test="chart-monthly-quota"
    >
      <div className={classes.errorRateChartLabel}>
        <div className={classes.chartTitle}>
          {intl.getI18nMessage('label.analytics.usage.monthly.quota.title')}
        </div>
        <div className={classes.metricsLabel}>
          {intl.getI18nMessage('label.analytics.lineChart.metric.text')}
        </div>
      </div>
      {!usageChartData.length && !isLoading ? (
          renderMessageComponent(showError, errorTitle, errorMessage)
        ) : (
          renderChartComponent()
        )}
    </div>
  );
}

MonthlyQuotaChart.propTypes = {
  defaultUsageResponse: PropTypes.array,
  apiMap: PropTypes.object,
  orgMap: PropTypes.object,
  isLoading: PropTypes.bool,
  fetchCustomDateMetricsData: PropTypes.func,
  usageResponseFor30DaysAgo: PropTypes.array,
  usageResponseFor52WeeksAgo: PropTypes.array,
  is30DaysDataLoading: PropTypes.bool,
  is52WeeksDataLoading: PropTypes.bool,
  usageParams: PropTypes.object,
};

export default MonthlyQuotaChart;